export const addDependencyModal = {
  extends: 'ModalWindow',
  width: '100%',
  minHeight: 'G3',
  maxWidth: 'I',
  tag: 'form',
  state: {
    name: '',
    version: '',
    schemaVersion: 'latest',
    type: 'dependencies',
    packages: [],
    isLoadingPackages: false,
    error: '',
    disabled: false
  },
  ModalHeader: {
    title: 'Add dependency',
    p: 'Symbols will make cloud build for your NPM dependency'
  },
  LoadingGifSection: {
    fullscreen: true,
    position: 'absolute',
    inset: '0',
    hide: (el, s) => !s.loading
  },
  Flex: {
    padding: 'A C3 0',
    flow: 'column',
    gap: 'B3',
    width: '70%',
    // The shared field kit (components/AppField.js): the caption label above
    // the control, the brand `field` fill with the hairline edge. The key
    // wiring moved WITH the fields: edit-dependency-modal.js extends this
    // page and overrides `'AppField.name'` BY KEY (a disabled `AppInput`, no
    // `Dropdown`), and the Dropdown's `onChoose` below reaches
    // `el.parent.AppInput` and `'AppField.schemaVersion'` → `.AppInput` by
    // key (add-dependency-modal.test.js, addModalsFieldKit.test.js).
    'AppField.name': {
      // An explicit base list turns the key's auto-extend off, so AppField
      // is named here beside the package search's DropdownParent.
      extends: ['AppField', 'DropdownParent'],
      ':focus-within': {
        zIndex: 1000,
        '& [dropdown]': {
          transform: 'translate3d(0,0,0)',
          opacity: 1,
          visibility: 'visible'
        }
      },
      label: (el) => el.call('polyglot', 'editor.addDependency.package'),
      AppInput: {
        required: true,
        value: '{{ name }}',
        placeholder: (el) => el.call('polyglot', 'editor.addDependency.packagePlaceholder'),
        onInput: (ev, el, s) => {
          const name = el.node.value
          s.update({
            name
          })
          if (name) {
            s.update({
              isLoadingPackages: true
            })

            if (name.length < 2) {
              return
            }

            const t = setTimeout(async () => {
              await el.call('searchNpmPackages', name)
              clearTimeout(t)
            }, 1000)
          } else {
            s.replace({
              packages: []
            })
          }
        }
      },
      Dropdown: {
        // The brand field's Group `childProps` gave the menu the field's
        // width; the kit field has no childProps, so the menu keeps it here.
        // `left` is a flat prop: an own prop beats the brand Dropdown's
        // `left: '50%'` at merge time (the `style: { left: 0 }` wrapper it
        // replaces relied on class order — FA108).
        width: '100%',
        left: '0',
        options: (el, s) => s.packages,
        onChoose: (ev, el, s, _ctx, calleeElement) => {
          const { name: pkgName, version: pkgVersion } = calleeElement.state
          s.update({
            name: pkgName,
            version: pkgVersion,
            schemaVersion: 'latest'
          })
          el.parent.AppInput.node.value = pkgName
          // LOOKUP-SHAPE-1: threw inline on a miss — guarded so choosing a
          // package can't crash the modal if the version field isn't
          // resolvable at this instant.
          const schemaVersionInput = el.lookup('AppField.schemaVersion')?.AppInput
          if (schemaVersionInput?.node) {
            schemaVersionInput.node.value = pkgVersion
              ? `^${pkgVersion}`
              : s.schemaVersion || 'latest'
          }
        },
        DropdownHeader: {
          if: (el, s) => s.packages.length === 0 && s.name.length > 2,
          text: '{{ editor.addDependency.noResults | polyglot }}'
        },
        ListInDropdown: {
          childProps: {
            isActive: (el, s) => s.parent.name === s.name,
            text: '{{ name }}',
            fontWeight: '400',
            Span: {
              opacity: '0.5',
              text: ' @{{ version }}'
            }
          }
        }
      }
    },
    'AppField.schemaVersion': {
      label: (el) => el.call('polyglot', 'editor.addDependency.version'),
      AppInput: {
        required: true,
        placeholder: (el) => el.call('polyglot', 'editor.addDependency.versionPlaceholder'),
        value: '{{ schemaVersion }}',
        onInput: (e, el, s) => {
          const schemaVersion = el.node.value
          s.update({
            schemaVersion
          })

          if (!s.name || s.name.length < 2) {
            return
          }

          const t = setTimeout(async () => {
            await el.call('fetchNpmPackage', s.name, schemaVersion)
            clearTimeout(t)
          }, 1000)
        }
      },
      // The resolved version is the kit's hint line under the control (the
      // brand field showed it as a bare text row). Both factories read
      // state, so the line follows every resolve and takes no space while
      // there is no version.
      FieldHint: {
        text: (el, s) => s.version || '',
        hide: (el, s) => !s.version
      }
    },
    Error: {
      text: (el, s) => s.error,
      color: 'red'
    }
  },
  ModalFooter: {
    margin: 'auto - -',
    CheckButton: {
      type: 'submit',
      reverse: true,
      icon: 'checkmark',
      text: '{{ common.action.save | polyglot }}',
      minHeight: 'controlH',
      padding: '0 A',
      fontSize: 'Z2'
    }
  },
  onSubmit: async (e, el, s, ctx) => {
    e.preventDefault()
    if (s.disabled) {
      return
    }

    const { name, version } = s

    await window.fetch(`https://pkg.symbo.ls/${name}@${version}`).then(async (res) => {
      if (!res.ok) {
        s.update({
          error: await res.text(),
          disabled: false
        })
        return
      }

      const [resolvedVersion] = res.url.substring(`https://pkg.symbo.ls/${name}@`.length).split('/')

      const value = s.version
      const obj = {
        key: name,
        value,
        resolvedVersion,
        type: s.type,
        version: s.schemaVersion
      }

      const exists = el.getDependencies(name)
      const action = exists ? 'updateItem' : 'addItem'

      try {
        await el.sdk[action]('dependencies', obj, {
          message: exists ? 'Updated dependency' : 'Added dependency'
        })

        await el.call('rerenderFileSidebarByType', 'dependencies')
        await el.call('closeRouterModal')
      } catch (err) {
        console.error(err)
        el.call('openNotification', {
          type: 'error',
          message: err.message
        })
      } finally {
        s.update({
          loading: false,
          disabled: false
        })
      }
    })
  }
}
